import type { ProductForm } from "./productForm";
import type { FieldSpan } from "@/components/ui/FormLayout";

/*
 * Bố cục biểu mẫu sản phẩm (19/09/2026, anh Toàn duyệt đề xuất "chuẩn" sau khi thấy trang Sửa sản phẩm lộn xộn).
 *
 * Chia nhóm theo NGƯỜI DÙNG nhóm đó, như hồ sơ vật tư của SAP (Dữ liệu cơ bản / Bán hàng / Mua hàng / Kho) và Odoo
 * (Chung / Bán / Mua / Kho), thay cho lưới 3 cột trải ngang gộp mọi thứ vào "Thông tin cơ bản". Trường ít ai điền gập vào
 * mục "Thêm" của nhóm — đo trên production 19/09/2026, 1.492 sản phẩm: mô tả tiếng Hoa, kho chứa, khuôn, giá DTD, giá
 * dưới MOQ 0%; EXW 0,3%; MSQ 0,2%; kích thước dây 0,6%; MCQ 6,6%; giá sàn 9,2%. Mục gập tự MỞ khi sản phẩm có dữ liệu ở
 * đó — không giấu dữ liệu của ai.
 *
 * Bảng này là NGUỒN của bố cục: biểu mẫu vẽ đúng theo nó, trang chi tiết dùng cùng thứ tự nhóm, và test bắt buộc mọi
 * trường của ProductForm nằm đúng một chỗ ở đây — thêm trường vào biểu mẫu mà quên xếp chỗ là test trượt.
 */

/** Một ô trên biểu mẫu. Vài ô mang hai trường dữ liệu (xem PRODUCT_FIELD_DATA). */
export type ProductFieldKey =
  | "image" | "productCode" | "subRef" | "customerRef" | "type" | "brandName" | "unit"
  | "description" | "color" | "colorZh" | "size" | "weightPer100" | "descriptionZh" | "wireSize"
  | "materialType" | "materialWidth" | "materialThickness" | "materialHardness"
  | "currency" | "sellingPrice" | "upCharge" | "moq" | "sampleLeadTime" | "bulkLeadTime"
  | "lowestPrice" | "dtdPrice" | "exwPrice" | "underMoqPrice" | "mcq" | "msq"
  | "supplierId" | "supplierProductRef" | "costUnitPrice"
  | "approvalStatus" | "approvalImage" | "stock" | "reorderPoint" | "location"
  | "storage" | "moldRef" | "moldLocation" | "remarks";

/** Độ rộng trên lưới 12 cột, theo độ dài dữ liệu: mã / số hẹp, mô tả cả hàng. */
export type ProductFieldSpan = FieldSpan;

export interface ProductFieldPlacement {
  key: ProductFieldKey;
  span: ProductFieldSpan;
}

export type ProductGroupId = "identity" | "spec" | "sales" | "purchasing" | "control";
export type ProductMoreId = "language" | "wire" | "material" | "salesMore" | "controlMore";

export interface ProductFormGroup {
  id: ProductGroupId;
  /** Cột trên màn rất rộng (≥ 1536 px, Tailwind 2xl); màn hẹp hơn xếp một cột: các nhóm cột trái trước, rồi cột phải. */
  column: "left" | "right";
  /** Nhóm chỉ hiện với người được xem giá vốn (máy chủ vẫn che giá vốn với người khác). */
  costOnly?: boolean;
  /** Ô đứng cạnh các ô khác của nhóm (ảnh sản phẩm cạnh mã hàng). */
  aside?: ProductFieldKey;
  fields: readonly ProductFieldPlacement[];
  /** Mục gập "Thêm": trường ít dùng. Tự mở khi có dữ liệu. */
  more?: readonly { id: ProductMoreId; fields: readonly ProductFieldPlacement[] }[];
}

export const PRODUCT_FORM_GROUPS: readonly ProductFormGroup[] = [
  {
    id: "identity", column: "left", aside: "image",
    fields: [
      { key: "productCode", span: 4 }, { key: "subRef", span: 4 }, { key: "customerRef", span: 4 },
      { key: "type", span: 4 }, { key: "brandName", span: 4 }, { key: "unit", span: 4 },
    ],
  },
  {
    id: "spec", column: "left",
    fields: [
      { key: "description", span: 12 },
      // Màu tiếng Hoa đứng ngay cạnh màu tiếng Anh (Roary 09/10/2026), không gập vào mục ngôn ngữ: 1.513 / 1.531 sản phẩm có
      // màu, nhân viên đang gõ lẫn hai thứ tiếng vào một ô vì thiếu chỗ.
      { key: "color", span: 6 }, { key: "colorZh", span: 6 }, { key: "size", span: 6 }, { key: "weightPer100", span: 6 },
    ],
    more: [
      // Ô "Mô tả chính" đã bỏ (Roary 12/10/2026): ngôn ngữ trên tem chọn lúc in, không phải cờ trên sản phẩm.
      { id: "language", fields: [{ key: "descriptionZh", span: 12 }] },
      { id: "wire", fields: [{ key: "wireSize", span: 12 }] },
      // Thông tin vật liệu (Roary 09/10/2026), theo đúng thứ tự khách viết.
      {
        id: "material",
        fields: [
          { key: "materialType", span: 6 }, { key: "materialWidth", span: 6 },
          { key: "materialThickness", span: 6 }, { key: "materialHardness", span: 6 },
        ],
      },
    ],
  },
  {
    id: "sales", column: "right",
    fields: [
      { key: "currency", span: 4 }, { key: "sellingPrice", span: 4 }, { key: "upCharge", span: 4 },
      { key: "moq", span: 4 }, { key: "sampleLeadTime", span: 4 }, { key: "bulkLeadTime", span: 4 },
    ],
    more: [
      {
        id: "salesMore",
        fields: [
          { key: "lowestPrice", span: 4 }, { key: "dtdPrice", span: 4 }, { key: "exwPrice", span: 4 },
          { key: "underMoqPrice", span: 4 }, { key: "mcq", span: 4 }, { key: "msq", span: 4 },
        ],
      },
    ],
  },
  {
    id: "purchasing", column: "right", costOnly: true,
    // Tên nhà cung cấp dài ("Dongguan Chang'an Button Factory") nên ô chọn chiếm cả hàng.
    fields: [{ key: "supplierId", span: 12 }, { key: "supplierProductRef", span: 6 }, { key: "costUnitPrice", span: 6 }],
  },
  {
    id: "control", column: "right",
    fields: [
      { key: "approvalStatus", span: 4 }, { key: "approvalImage", span: 8 },
      { key: "stock", span: 4 }, { key: "reorderPoint", span: 4 }, { key: "location", span: 4 },
      { key: "remarks", span: 12 },
    ],
    more: [{ id: "controlMore", fields: [{ key: "storage", span: 4 }, { key: "moldRef", span: 4 }, { key: "moldLocation", span: 4 }] }],
  },
];

/** Trường dữ liệu của ProductForm mà mỗi ô mang. Ô "stock" chỉ đọc (Kho & LOT là nguồn tồn) nên không mang trường nào. */
export const PRODUCT_FIELD_DATA: Record<ProductFieldKey, readonly (keyof ProductForm)[]> = {
  image: ["imageUrl", "imageKey"], productCode: ["productCode"], subRef: ["subRef"], customerRef: ["customerRef"],
  type: ["type"], brandName: ["brandName"], unit: ["unit"],
  description: ["description"], color: ["color"], colorZh: ["colorZh"], size: ["size"], weightPer100: ["weightPer100", "weightUnit"],
  descriptionZh: ["descriptionZh"], wireSize: ["wireSize"],
  materialType: ["materialType"], materialWidth: ["materialWidth", "materialWidthUnit"],
  materialThickness: ["materialThickness", "materialThicknessUnit"], materialHardness: ["materialHardness"],
  currency: ["currency"], sellingPrice: ["sellingPrice"], upCharge: ["upCharge"], moq: ["moq"],
  sampleLeadTime: ["sampleLeadTime"], bulkLeadTime: ["bulkLeadTime"],
  lowestPrice: ["lowestPrice"], dtdPrice: ["dtdPrice"], exwPrice: ["exwPrice"], underMoqPrice: ["underMoqPrice"], mcq: ["mcq"], msq: ["msq"],
  supplierId: ["supplierId"], supplierProductRef: ["supplierProductRef"], costUnitPrice: ["costUnitPrice"],
  approvalStatus: ["approvalStatus"], approvalImage: ["approvalImageUrl", "approvalImageKey"], stock: [],
  reorderPoint: ["reorderPoint"], location: ["location"],
  storage: ["storage"], moldRef: ["moldRef"], moldLocation: ["moldLocation"], remarks: ["remarks"],
};

/** Mọi ô của một nhóm theo thứ tự hiển thị: ô bên cạnh, các ô chính, rồi các mục gập. */
export function groupFieldKeys(group: ProductFormGroup): ProductFieldKey[] {
  return [
    ...(group.aside ? [group.aside] : []),
    ...group.fields.map((f) => f.key),
    ...(group.more ?? []).flatMap((m) => m.fields.map((f) => f.key)),
  ];
}

/**
 * Ô có dữ liệu chưa. Kích thước dây tính khi có ít nhất một số đo (bản lưu luôn mang đơn vị mm kể cả khi trống).
 */
export function productFieldFilled(form: ProductForm, key: ProductFieldKey): boolean {
  if (key === "wireSize") return Object.entries(form.wireSize).some(([k, v]) => !k.endsWith("Unit") && String(v ?? "").trim() !== "");
  if (key === "weightPer100") return form.weightPer100.trim() !== "";
  // Đơn vị luôn mang sẵn mm nên chỉ tính số.
  if (key === "materialWidth") return form.materialWidth.trim() !== "";
  if (key === "materialThickness") return form.materialThickness.trim() !== "";
  if (key === "image") return form.imageUrl.trim() !== "";
  if (key === "approvalImage") return form.approvalImageUrl.trim() !== "";
  return PRODUCT_FIELD_DATA[key].some((field) => String(form[field] ?? "").trim() !== "");
}

/** Số ô đã có dữ liệu trong một mục gập — hiện cạnh tên mục, và mục có dữ liệu thì mở sẵn. */
export const filledCount = (form: ProductForm, fields: readonly ProductFieldPlacement[]) =>
  fields.filter((f) => productFieldFilled(form, f.key)).length;
